import { type ComponentPropsWithRef } from 'react';
import { type AriaRadioGroupProps, useRadioGroup } from 'react-aria';
import { useRadioGroupState } from 'react-stately';

import { clsx } from '../../../lib/clsx';

import styles from './SegmentedControl.module.css';

import { type SegmentedControlOption, SegmentedControlSegment } from './SegmentedControlSegment';

interface SegmentedControlProps
  extends
    Omit<AriaRadioGroupProps, 'orientation'>,
    Pick<ComponentPropsWithRef<'div'>, 'ref' | 'style'> {
  className?: string;
  options: SegmentedControlOption[];
}

/**
 * Pill-shaped radio group with equal segments and a sliding indicator behind the selected one.
 */
export function SegmentedControl(props: SegmentedControlProps) {
  const radioGroupState = useRadioGroupState(props);
  const radioGroup = useRadioGroup({ ...props, orientation: 'horizontal' }, radioGroupState);

  const selectedIndex = props.options.findIndex((option) => {
    return option.value === radioGroupState.selectedValue;
  });

  return (
    <div
      {...radioGroup.radioGroupProps}
      ref={props.ref}
      className={clsx(styles.root, props.className)}
      style={{
        ...props.style,
        '--prop-segment-count': props.options.length,
        '--prop-selected-index': selectedIndex,
      }}
      data-has-selection={selectedIndex !== -1}
    >
      <span className={styles.indicator} aria-hidden="true" />

      {props.options.map((option) => {
        return (
          <SegmentedControlSegment key={option.value} option={option} state={radioGroupState} />
        );
      })}
    </div>
  );
}
